import {
  Accordion,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
  useEventCallback,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import {
  transformCertificate,
  transformCertificateAnsc,
} from 'civil-status/utils/transform-certificate';
import { transformCertificateTitle } from 'civil-status/utils/transform-certificate-title';
import {
  CertificateRadio,
  CertificateType,
  CertificationFormVM,
} from 'civil-status/view-models/document-birth-form';
import { setNestedObjectValues, useFormikContext } from 'formik';
import ModalInfoCertification from './info-certificate';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { memo, useEffect, useMemo, useState } from 'react';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import PmiTextField from 'shell/components/shared/text-field';
import {
  __ACT_API_URL__,
  __CERTIFICATION_API_URL__,
  __CIVIL_STATUS_API_URL__,
} from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { DocumentRegistry } from 'shell/models/document-registry';
import { ItemVM } from 'shell/view-models/item';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore, Visibility, VisibilityOff } from '@mui/icons-material';
import AccordionCertificateType from '../accordion-certificate-type';
import Payment from './fragment-payment';
import ModalPrintSuccess from './request-print';
import TimberSelect from 'shell/components/shared/select-timber';
import TimberFreeSelect from 'shell/components/shared/select-timber-free';
import NumberCopiesSelect from 'shell/components/shared/select-copies';
import {
  ID_COPIA_INTEGRALE_CITTADINANZA,
  ID_COPIA_INTEGRALE_MATRIMONIO,
  ID_COPIA_INTEGRALE_MORTE,
  ID_COPIA_INTEGRALE_NASCITA,
  M1,
  M2,
  N1,
  N2,
  N4,
  M4,
  D4,
  M3,
  N3,
  U2,
  U3,
  ID_ESTRATTO_CITTADINANZA,
  ID_ESTRATTO_MORTE,
} from '../accordion-certificate-type/civil-status-certificate';
import ListAnsc from 'civil-status/components/shared/list-ansc';
import ErrorModal from 'shell/components/shared/modal-error';
import SiglaSelect from './select-sigle';
import { ActOperator } from 'shell/models/act-list';

interface PrintDocumentPageProps {
  onBack: () => void;
}

const PrintDocumentPage = ({ onBack }: PrintDocumentPageProps): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const { userProfile } = useUserProfile();

  const [isVisibility, setIsVisibility] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(true);

  const [printIndex, setPrintIndex] = useState<number>(0);

  const codiceDocumento: string = useMemo(() => {
    return formContext.values.certificate?.element?.key.codiceDocumento?.toString() || '';
  }, [formContext.values.certificate?.element?.key.codiceDocumento]);

  const isAnscDocument: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.isAnsc || false;
  }, [formContext.values.chosenDocument?.isAnsc]);

  const onVisibilityPress = useEventCallback(() => {
    setIsVisibility(!isVisibility);
  });

  const clearPrintDocument = useEventCallback(
    (copies?: string, certificate?: CertificateRadio | null) => {
      formContext.setValues({
        ...formContext.values,
        timber: '',
        releaseMode: null,
        timberFreeText: '',
        copies: copies || '1',
        reason: '',
        flagFincatura: false,
        flagOmittedSign: false,
        flagPaternita: false,
        flagUsePublication: false,
        typePayment: '',
        requiredTypeUse: false, // reset campo di controllo
        typeUse: '',
        certificate: certificate || null,
        total: 0,
      });
    }
  );

  const [series] = useHttpFetch<HttpResponseList<DocumentRegistry>, Array<ItemVM>>(
    `${__CIVIL_STATUS_API_URL__}/registro-atto`,
    {
      onBootstrap: true,
      options: {
        method: 'GET',
      },
      transformer: (response) =>
        (response?.data?.payload?.list || []).map((item) => ({
          id: item.key?.codiceRegistro || '',
          value: item.registro || '',
        })),
    }
  );

  const seriesData = (series.data as Array<ItemVM>) || [];

  const registryItem = seriesData.find(
    (item) =>
      item.id === formContext.values.chosenDocument?.actNumber?.split('-')[1] &&
      !formContext.values.chosenDocument.isAnsc
  );

  const print = useHttpClient<Blob>(`${__CIVIL_STATUS_API_URL__}/stampa`, {
    options: { loading: true, method: 'POST', errorCatching: true, data: {} },
  });

  const printAnsc = useHttpClient<HttpResponseList<{ contenuto: string }>>(
    `${__CERTIFICATION_API_URL__}/certificazione/stampa-ansc`,
    {
      options: { loading: true, method: 'POST', errorCatching: true, data: {} },
    }
  );

  const [pdfList, setPdfList] = useState<Array<string>>([]);

  useEffect(() => {
    clearPrintDocument();
  }, []);

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  // InfoCertification modal
  const [infoCertification, setInfoCertification] = useState<{
    open: boolean;
    total?: number;
    type?: string;
  }>({
    open: false,
  });

  const [warningModal, setWarningModal] = useState<boolean>(false);

  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);

  const onPrint = useEventCallback(() => {
    formContext.validateForm(formContext.values).then((validationErrors) => {
      formContext.setTouched(setNestedObjectValues(validationErrors, true));

      if (Object.values(validationErrors).length > 0) {
        return;
      }

      if (isAnscDocument) {
        const data = transformCertificateAnsc(formContext.values);

        setPrintIndex(0);

        (
          printAnsc({
            data,
          }) as ResponsePromise<
            HttpResponseList<{
              contenuto: string;
            }>
          >
        ).then((response) => {
          if ((response?.data?.payload?.list?.length || 0) > 0) {
            setPdfList(response?.data?.payload?.list?.map((el) => el.contenuto) || []);
          }
        });

        return;
      }

      const isNotFlagSign = formContext?.values?.flagOmittedSign;
      const nomeOperatore = userProfile?.datiLogin?.nome;

      const data = transformCertificate(
        formContext.values,
        isNotFlagSign,
        nomeOperatore,
        userProfile.datiLogin?.key?.userName || '',
        registryItem
      );

      (
        print({
          data,
          responseType: 'blob',
        }) as ResponsePromise<Blob>
      ).then((response) => {
        if (response?.data) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Certificazione Stato Civile',
              open: true,
            });
          });
        }
      });
    });
  });

  const atto = useMemo(() => {
    return transformCertificateTitle({
      type: formContext.values?.chosenDocument?.element?.codiceAtto as CertificateType,
      element: formContext.values.certificate?.element,
    });
  }, [
    formContext.values?.chosenDocument?.element?.codiceAtto,
    formContext.values.certificate?.element,
  ]);
  // useEffect(() => {
  //   if (formContext.values.flagInternal) {
  //     formContext.setFieldValue('releaseMode', DEFAULT_PPAA2);
  //   }
  // }, [formContext.values.flagInternal]);

  // useEffect(() => {
  //   if (formContext.values.releaseMode?.id === ID_PPAA2) {
  //     formContext.setFieldValue('typePayment', undefined);
  //   }
  // }, [formContext.values.releaseMode]);

  // const handleTypePayment = useEventCallback((event: SelectChangeEvent<string>) => {
  //   formContext.setFieldValue('typePayment', event.target.value);
  // });

  const difference = useMemo(() => {
    const numberCopies = formContext.values.copies
      ? Number.parseInt(formContext.values.copies, 10)
      : 0;
    const differenceNumber = numberCopies - pdfList.length;
    return differenceNumber;
  }, [formContext.values.copies, pdfList.length]);

  useEffect(() => {
    if (pdfList.length > 0 && difference !== 0) {
      setWarningModal(true);
    } else if (pdfList.length > 0) {
      const stringCertificate =
        pdfList.length <= 1
          ? `Certificazione Stato Civile`
          : `Certificazione Stato Civile - copia ${printIndex + 1} di ${pdfList.length}`;

      setPdfModal({
        data: pdfList[printIndex],
        name: stringCertificate,
        open: true,
      });
    }
  }, [pdfList, difference]);

  useEffect(() => {
    if (
      formContext.values.certificate?.element?.key.codiceDocumento?.toString() ===
        ID_COPIA_INTEGRALE_CITTADINANZA ||
      formContext.values.certificate?.element?.key.codiceDocumento?.toString() ===
        ID_COPIA_INTEGRALE_MORTE ||
      formContext.values.certificate?.element?.key.codiceDocumento?.toString() ===
        ID_COPIA_INTEGRALE_NASCITA ||
      formContext.values.certificate?.element?.key.codiceDocumento?.toString() ===
        ID_COPIA_INTEGRALE_MATRIMONIO
    ) {
      clearPrintDocument(formContext.values.copies, formContext.values.certificate);
    }
  }, [formContext.values.certificate?.element?.key.codiceDocumento]);

  const handleTypeUse = useEventCallback((item?: ItemVM) => {
    formContext.setFieldValue('typeUse', item?.id?.toString());
  });

  const isVisibilityTypeUse: boolean = useMemo(() => {
    const res = isAnscDocument && (codiceDocumento.includes('1') || codiceDocumento.includes('2'));

    formContext.setFieldValue('requiredTypeUse', res || false);
    return res;
  }, [isAnscDocument, codiceDocumento]);

  const isSignature: boolean = useMemo(() => {
    return (
      codiceDocumento === M2 ||
      codiceDocumento === M3 ||
      codiceDocumento === N2 ||
      codiceDocumento === N3 ||
      codiceDocumento === U2 ||
      codiceDocumento === U3 ||
      codiceDocumento === ID_ESTRATTO_CITTADINANZA ||
      codiceDocumento === ID_COPIA_INTEGRALE_CITTADINANZA ||
      codiceDocumento === ID_COPIA_INTEGRALE_MORTE ||
      codiceDocumento === ID_ESTRATTO_MORTE
    );
  }, [codiceDocumento]);

  const disabledPrint: boolean = useMemo(() => {
    if (isSignature) {
      return !Boolean(formContext.values.user && formContext.values.pin && formContext.values.otp);
    }
    return false;
  }, [isSignature, formContext.values.user, formContext.values.pin, formContext.values.otp]);

  const getOperator = useHttpClient(`${__ACT_API_URL__}/operatore-usc`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  useEffect(() => {
    if (isSignature && !formContext.values.user) {
      (getOperator() as ResponsePromise<HttpResponse<ActOperator>>).then((response) => {
        const castResp = response?.data.payload as ActOperator;

        if (castResp.codiceFirma) {
          formContext.setFieldValue('user', castResp.codiceFirma || '');
        }
      });
    }
  }, [isSignature, formContext.values.user]);

  useEffect(() => {
    if (isSignature) {
      formContext.setFieldValue('copies', '1');
    }
  }, [isSignature]);

  return (
    <>
      <Payment />
      <ErrorModal
        title="Warning"
        message={`Sono state stampate ${pdfList.length} di ${formContext.values.copies}`}
        id="warning-copies"
        open={warningModal}
        onClose={() => {
          setWarningModal(false);
          setPdfModal({
            data: pdfList[printIndex],
            name: `Certificazione Stato Civile - copia ${printIndex + 1} di ${pdfList.length}`,
            open: true,
          });
        }}
      />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Richiesta Certificato
      </Typography>
      <AccordionCertificateType />
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls={`panel-release-mode`}
          id={`panel-release-mode`}
          expandIcon={<ExpandMore />}
        >
          <Typography>Modalità rilascio</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {isAnscDocument &&
            (codiceDocumento === N1 || codiceDocumento === N2) &&
            formContext.values.chosenDocument?.typeAct === 'Atto di nascita' ? (
              <Grid item xs={12} container spacing={1} display="flex" alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="flagPaternita"
                        checked={formContext.values.flagPaternita || false}
                        onChange={() => {
                          formContext.setFieldValue(
                            'flagPaternita',
                            !formContext.values.flagPaternita
                          );
                        }}
                        inputProps={{ 'aria-label': 'Stampa con paternità e maternità' }}
                      />
                    }
                    label="Stampa con paternità e maternità"
                  />
                </Grid>
              </Grid>
            ) : undefined}
            {isAnscDocument &&
            (codiceDocumento === N1 ||
              codiceDocumento === N2 ||
              codiceDocumento === M1 ||
              codiceDocumento === M2) ? (
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="flagUsePublication"
                      checked={formContext.values.flagUsePublication || false}
                      onChange={() =>
                        formContext.setFieldValue(
                          'flagUsePublication',
                          !formContext.values.flagUsePublication
                        )
                      }
                      inputProps={{ 'aria-label': 'Uso pubblicazione' }}
                    />
                  }
                  label="Uso pubblicazione"
                />
              </Grid>
            ) : undefined}
            {isAnscDocument &&
            (codiceDocumento === N4 || codiceDocumento === M4 || codiceDocumento === D4) ? (
              <>
                <Grid item xs={12} container spacing={1} display="flex" alignItems="center">
                  <Grid item xs={12} md={4}>
                    <PmiTextField
                      aria-label="Altre annunciazioni dell'atto"
                      label="Altre annunciazioni dell'atto"
                      id="annunciazioniAtto"
                      name="annunciazioniAtto"
                      fullWidth
                      type="text"
                      variant="outlined"
                      multiline
                      rows={3}
                      error={
                        formContext.touched?.annunciazioniAtto &&
                        Boolean(formContext.errors?.annunciazioniAtto)
                      }
                      helperText={formContext.errors?.annunciazioniAtto}
                      value={formContext.values.annunciazioniAtto || ''}
                      onChange={(event) =>
                        formContext.setFieldValue('annunciazioniAtto', event?.target.value)
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={1} display="flex" alignItems="center">
                  <Grid item xs={12} md={4}>
                    <SiglaSelect
                      value={formContext.values?.sigla || ''}
                      onChange={(value) => {
                        formContext.setFieldValue('sigla', value);
                      }}
                      error={Boolean(formContext.errors.sigla) && formContext.touched.sigla}
                      required
                    />
                  </Grid>
                </Grid>
              </>
            ) : undefined}
            {isVisibilityTypeUse ? (
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={12} md={6}>
                  <ListAnsc
                    label="Tipologia d'uso"
                    idDominio="113"
                    value={formContext.values?.typeUse || ''}
                    onChange={handleTypeUse}
                    error={Boolean(formContext.errors.typeUse) && formContext.touched.typeUse}
                    required
                  />
                </Grid>
              </Grid>
            ) : undefined}
            {isAnscDocument ? undefined : (
              <>
                <Grid item xs={12}>
                  <TimberSelect
                    value={formContext.values.timber || ''}
                    onChange={(value) => formContext.setFieldValue('timber', value)}
                    certificate={formContext.values.selectedCertificate || ''}
                    error={formContext.touched?.timber && Boolean(formContext.errors?.timber)}
                    actType={atto.type?.replaceAll(' ', '')?.toUpperCase()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TimberFreeSelect
                    value={formContext.values.timberFreeText || ''}
                    onChange={(value, item) =>
                      formContext.setValues({
                        ...formContext.values,
                        timberFreeText: value,
                        reason: item?.value,
                        modifierCode: item?.itemField?.codiceModificatore,
                      })
                    }
                    error={
                      formContext.touched?.timberFreeText &&
                      Boolean(formContext.errors?.timberFreeText)
                    }
                    actType={atto.type?.replaceAll(' ', '')?.toUpperCase()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PmiTextField
                    aria-label="Motivo"
                    label="Motivo"
                    id="reason"
                    name="reason"
                    fullWidth
                    type="text"
                    variant="outlined"
                    multiline
                    rows={3}
                    error={formContext.touched?.reason && Boolean(formContext.errors?.reason)}
                    helperText={formContext.errors?.reason}
                    value={formContext.values.reason || ''}
                    onChange={(event) => formContext.setFieldValue('reason', event?.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} container spacing={1} display="flex" alignItems="center">
              <Grid item xs={12} md={8}>
                <NumberCopiesSelect
                  value={formContext.values.copies || ''}
                  onChange={(value) => formContext.setFieldValue('copies', value)}
                  disabled={isSignature}
                />
              </Grid>
            </Grid>
            {isAnscDocument ? undefined : (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="flagCarta"
                      checked={formContext.values.flagCarta || false}
                      disabled={formContext.values.flagInternal}
                      onChange={() => {
                        formContext.setFieldValue('flagCarta', !formContext.values.flagCarta);
                      }}
                      inputProps={{ 'aria-label': 'carta zero' }}
                    />
                  }
                  label="Carta zero"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="flagOmittedSign"
                      checked={formContext.values.flagOmittedSign || false}
                      onChange={() => {
                        formContext.setFieldValue(
                          'flagOmittedSign',
                          !formContext.values.flagOmittedSign
                        );
                      }}
                      inputProps={{ 'aria-label': 'stampa firma omessa' }}
                    />
                  }
                  label="Stampa firma omessa"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="flagFincatura"
                      checked={formContext.values.flagFincatura || false}
                      onChange={() => {
                        formContext.setFieldValue(
                          'flagFincatura',
                          !formContext.values.flagFincatura
                        );
                      }}
                      inputProps={{ 'aria-label': 'stampa fincatura' }}
                    />
                  }
                  label="Stampa fincatura"
                />
              </Grid>
            )}
            {isSignature ? (
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="Utenza"
                    label="Utenza *"
                    id="user"
                    name="user"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={formContext.values.user || ''}
                    onChange={(event) =>
                      formContext.setFieldValue('user', event.target.value || '')
                    }
                    error={formContext.touched?.user && Boolean(formContext.errors?.user)}
                    helperText={formContext.errors?.user}
                  />
                </Grid>
                <Grid item xs={12} md={4} display="flex">
                  <span style={{ width: '85%' }}>
                    <PmiTextField
                      aria-label="PIN"
                      label="PIN *"
                      id="pin"
                      name="pin"
                      fullWidth
                      type={isVisibility ? 'text' : 'password'}
                      variant="outlined"
                      value={formContext.values.pin || ''}
                      onChange={(event) =>
                        formContext.setFieldValue('pin', event.target.value || '')
                      }
                      error={formContext.touched?.pin && Boolean(formContext.errors?.pin)}
                      helperText={formContext.errors?.pin}
                    />
                  </span>
                  <span style={{ paddingLeft: '10px', width: '15%' }}>
                    <IconButton
                      aria-label="visualizza/nascondi PIN"
                      component="span"
                      onClick={() => onVisibilityPress()}
                    >
                      {isVisibility ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </span>
                </Grid>
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    aria-label="OTP"
                    label="OTP"
                    id="otp"
                    name="otp"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={formContext.values.otp || ''}
                    onChange={(event) => formContext.setFieldValue('otp', event.target.value || '')}
                    error={formContext.touched?.otp && Boolean(formContext.errors?.otp)}
                    helperText={formContext.errors?.otp}
                  />
                </Grid>
              </Grid>
            ) : undefined}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} display="flex">
          <div>
            <PmiButton variant="outlined" color="secondary" onClick={() => onBack()}>
              Indietro
            </PmiButton>
          </div>
          <div className="pmi-pl-3">
            <PmiButton
              variant="contained"
              color="cdmBrandThird"
              onClick={onPrint}
              disabled={disabledPrint}
            >
              Stampa
            </PmiButton>
          </div>
        </Grid>
      </Grid>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title={pdfModal.name}
        customPrint={pdfList.length ? `Stampa ${printIndex + 1}` : undefined}
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });

          setOpenSuccessModal(true);
        }}
      />
      <ModalPrintSuccess
        open={openSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
        }}
        onYes={() => {
          const {
            amountRightsSecretariat = 0.0,
            amountRightsCivilState = 0.0,
            amountFixedRights = 0.0,
            amountExpenses = 0.0,
            amountTaxStamp = 0.0,
          } = formContext.values.releaseMode || {};

          const rights =
            amountExpenses +
            amountFixedRights +
            amountRightsCivilState +
            amountRightsSecretariat +
            amountTaxStamp;

          const parsedTotal = formContext.values?.flagInternal
            ? Number.parseFloat('0,00')
            : Number.parseFloat(rights?.toString() || '0,00');

          formContext.setFieldValue('total', parsedTotal);

          if (pdfList.length > 1 && printIndex < pdfList.length) {
            setOpenSuccessModal(false);

            setInfoCertification({ open: true, total: parsedTotal });

            setPrintIndex(printIndex + 1);

            return;
          }

          setOpenSuccessModal(false);

          setInfoCertification({ open: true, total: parsedTotal });
        }}
      />
      <ModalInfoCertification
        open={infoCertification.open}
        onClose={() => {
          if (pdfList.length > 1 && printIndex < pdfList.length) {
            setPdfModal({
              data: pdfList[printIndex],
              name: `Certificazione Stato Civile - copia ${printIndex + 1} di ${pdfList.length}`,
              open: true,
            });

            setInfoCertification({ open: false });

            return;
          } else if (pdfList.length > 1) {
            setInfoCertification({ open: false });

            setPdfList([]);
            return;
          }
          setInfoCertification({ open: false });
        }}
        title={formContext.values.certificate?.value}
        amountCertifate={infoCertification.total?.toFixed(2)}
        showAmount
      />
    </>
  );
};

PrintDocumentPage.displayName = 'Print Document Page';

const PrintDocument = memo(PrintDocumentPage);

export default PrintDocument;
